import React from 'react';
import { Text, TouchableOpacity, Platform, StyleSheet, View } from 'react-native';
import dayjs from 'dayjs';
import { Ionicons, MaterialIcons } from '@expo/vector-icons';
import { useMyTimeTableStore } from 'src/domain/stores/useMyTimeTableStore';
import { useNavigationStore } from 'src/domain/stores/useNavigationStore';
import Fonts from 'src/core/utils/Fonts';
import Dimens from 'src/core/utils/Dimens';
import Colors from 'src/core/utils/Colors';
import Constants from 'src/core/utils/Constants';
import type { ApiPageProgramItem } from 'src/core/models/api/ApiPage';

interface ProgramItemProps {
	data: ApiPageProgramItem;
	noBorderBottom?: boolean;
	flag?: boolean;
	/** Leave out the zaal, for a list that already groups its rows under one. */
	hideStage?: boolean;
	onPress?: (type: string, data: ApiPageProgramItem) => void;
}

function ProgramItem({ data, noBorderBottom, flag, hideStage, onPress }: ProgramItemProps) {
	const navigationStore = useNavigationStore();

	// Derive directly from the store so the heart reflects optimistic toggles, server reconciliation and
	// external changes without a stale local copy.
	const favorite = useMyTimeTableStore((state) => state.myTimeTableData.some((page) => page.pageid === data.id));
	const toggleFavorite = useMyTimeTableStore((state) => state.toggleFavorite);
	// When the server favorites could not be loaded, the like-state is unknown: dim the heart.
	const favoritesDisabled = useMyTimeTableStore((state) => state.loadFailed);

	const getTimeFromTimestamp = (time: number) => {
		return dayjs.unix(time).format(Constants.timeFormat);
	};

	const onToggleFavorite = () => {
		toggleFavorite(data.id, data.event_uuid).catch(() => undefined);
	};

	const goToPage = (item: ApiPageProgramItem) => {
		navigationStore.setNotificationOrigin('');
		if (onPress) onPress(item.type, item);
	};

	return (
		<TouchableOpacity
			style={noBorderBottom ? [styles.noLine, styles.listItem] : styles.listItem}
			// Also a session the beheerder left empty: its page carries the title, the time, the zaal and the
			// heart, and says "geen inhoud" where the text would be. A dead row told the participant nothing.
			onPress={() => goToPage(data)}
		>
			<View style={styles.body}>
				{/* A session title can run to four lines; two keep the rows scannable and the times in view. */}
				<Text numberOfLines={2} style={styles.heading}>
					{data.name}
				</Text>
				{data.data.stage && !hideStage ? <Text style={styles.description}>{data.data.stage}</Text> : null}
				{data.data.start_time && data.data.end_time ? (
					<Text
						style={
							!flag ? styles.description : [styles.description, { color: Colors.red, fontFamily: Fonts.montserratMedium }]
						}
					>
						{getTimeFromTimestamp(data.data.start_time)} - {getTimeFromTimestamp(data.data.end_time)}
					</Text>
				) : null}
			</View>
			<View style={styles.right}>
				<TouchableOpacity
					style={[styles.favoriteIconContainer, favoritesDisabled ? styles.favoriteDisabled : null]}
					onPress={onToggleFavorite}
				>
					{Platform.OS === 'ios' ? (
						<Ionicons name={favorite ? 'heart' : 'heart-outline'} size={26} style={{ color: Colors.red }} />
					) : (
						<MaterialIcons name="favorite" size={24} style={favorite ? { color: Colors.red } : { color: Colors.overlay12 }} />
					)}
				</TouchableOpacity>
			</View>
		</TouchableOpacity>
	);
}

export default ProgramItem;

const styles = StyleSheet.create({
	noLine: {
		borderBottomWidth: Platform.OS === 'ios' ? 0 : undefined,
	},
	listItem: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		paddingVertical: Dimens.padding12,
		marginHorizontal: Dimens.margin20,
		paddingRight: Dimens.padding5,
		borderBottomWidth: 1,
		borderColor: Colors.greyBorder,
	},
	body: {
		flex: 1,
	},
	right: {
		width: 54,
		height: 54,
		justifyContent: 'center',
		alignItems: 'center',
	},
	favoriteIconContainer: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: 54,
		height: 54,
	},
	favoriteDisabled: {
		opacity: 0.35,
	},
	heading: {
		fontSize: Platform.OS === 'ios' ? Dimens.fontSize17 : Dimens.fontSize16,
		color: Colors.blackPure,
		fontFamily: Fonts.montserratRegular,
		paddingBottom: Dimens.padding5,
	},
	description: {
		color: Platform.OS === 'ios' ? Colors.greyTextDark : Colors.overlay54,
		fontSize: Platform.OS === 'ios' ? Dimens.fontSize12 : Dimens.fontSize14,
		fontFamily: Fonts.montserratRegular,
	},
});
